/**
 * A porta entre a tela de inspeções e a API do módulo 10.
 */
import { pedir } from '@/lib/pedido';

export type RotinaDoChecklist = 'diaria' | 'semanal' | 'entrega' | 'devolucao' | 'pos_manutencao';
export type SituacaoDoChecklist = 'rascunho' | 'publicado' | 'arquivado';
export type TipoDoItem = 'conformidade' | 'medida' | 'texto';
export type Criticidade = 'informativo' | 'atencao' | 'critico';
export type SituacaoDaInspecao = 'em_andamento' | 'concluida' | 'cancelada';
export type SituacaoDaOcorrencia = 'aberta' | 'em_tratamento' | 'resolvida' | 'descartada';
export type Desfecho = 'ordem_de_servico' | 'resolvida_no_local' | 'nao_procede';

export interface Checklist {
  readonly id: string;
  readonly nome: string;
  readonly rotina: RotinaDoChecklist;
  readonly situacao: SituacaoDoChecklist;
  readonly modeloId: string | null;
  readonly modelo: string | null;
  readonly configuracaoId: string | null;
  readonly configuracao: string | null;
  readonly notaDeRevisao: string | null;
  readonly publicadoEm: string | null;
  readonly itens: number;
  readonly criticos: number;
}

export interface ItemDoChecklist {
  readonly id: string;
  readonly ordem: number;
  readonly grupo: string;
  readonly descricao: string;
  readonly tipo: TipoDoItem;
  readonly unidade: string | null;
  readonly criticidade: Criticidade;
}

/** O 409 de `POST /inspecoes` quando a inspecao do dia ja esta aberta: a mesa oferece continuar. */
export const CODIGO_INSPECAO_EM_ANDAMENTO = 'inspecao_em_andamento';

export interface Inspecao {
  readonly id: string;
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly checklist: string;
  readonly rotina: RotinaDoChecklist;
  readonly dia: string;
  readonly situacao: SituacaoDaInspecao;
  readonly concluidaEm: string | null;
  readonly respondidos: number;
  readonly ocorrencias: number;
  readonly criticasAbertas: number;
}

/** O que já foi respondido de um item da inspeção (`GET /inspecoes/:id/itens`). */
export interface RespostaGravada {
  readonly conforme: boolean | null;
  readonly naoSeAplica: boolean;
  readonly medida: number | null;
  readonly texto: string;
}

/** A inspeção do dia que não foi cancelada (o índice `inspecao_viva_por_dia`). */
export interface InspecaoViva {
  readonly id: string;
  readonly situacao: 'em_andamento' | 'concluida';
  /** `HH:MM` no fuso da operação; nula em andamento. */
  readonly concluidaAs: string | null;
  readonly feitaPor: string | null;
  readonly respondidos: number;
  readonly total: number;
}

export interface ItemDaInspecao extends ItemDoChecklist {
  readonly resposta: RespostaGravada | null;
}

export interface Ocorrencia {
  readonly id: string;
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly descricao: string;
  /** Instantâneo: a criticidade que o item tinha no dia, não a de hoje. */
  readonly criticidade: 'atencao' | 'critico';
  readonly situacao: 'aberta' | 'em_tratamento';
  readonly pedeBloqueio: boolean;
  readonly ordemId: string | null;
  readonly ordem: number | null;
  readonly criadoEm: string;
  readonly diasAberta: number;
  readonly diaDaInspecao: string | null;
  readonly checklist: string | null;
}

export interface LinhaDeCobertura {
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly checklistId: string;
  readonly checklist: string;
  readonly rotina: RotinaDoChecklist;
  /** Nulo quando a máquina nunca foi inspecionada por este checklist. */
  readonly ultimaEm: string | null;
  readonly diasSemInspecao: number | null;
  readonly inspecoes: number;
}

export interface EvidenciaDaOcorrencia {
  readonly id: string;
  readonly descricao: string;
  readonly mime: string;
  readonly tamanhoBytes: number;
  readonly enviadoEm: string;
  readonly enviadoPor: string | null;
}

export const api = {
  evidencias: (ocorrenciaId: string) =>
    pedir<{ evidencias: readonly EvidenciaDaOcorrencia[] }>(
      `/inspecao/ocorrencias/${encodeURIComponent(ocorrenciaId)}/evidencias`,
    ),

  /**
   * O corpo é o ARQUIVO CRU, e a descrição vai no endereço. Sem `FormData`:
   * o servidor lê o tipo do conteúdo e calcula o hash, e um envelope
   * multipart só acrescentaria uma camada para o nome do arquivo mentir.
   */
  anexarEvidencia: (ocorrenciaId: string, descricao: string, arquivo: File) =>
    pedir<{ id: string; descricao: string; mime: string }>(
      `/inspecao/ocorrencias/${encodeURIComponent(ocorrenciaId)}/evidencias?descricao=${encodeURIComponent(descricao)}`,
      { method: 'POST', body: arquivo, headers: { 'content-type': arquivo.type || 'application/octet-stream' } },
    ),

  /** Devolve uma URL que vale cinco minutos, e não o arquivo. */
  urlDaEvidencia: (id: string) =>
    pedir<{ url: string; expiraEmSegundos: number; mime: string }>(
      `/inspecao/evidencias/${encodeURIComponent(id)}/arquivo`,
    ),

  checklists: () => pedir<{ checklists: readonly Checklist[] }>('/inspecao/checklists'),
  itensDoChecklist: (id: string) =>
    pedir<{ itens: readonly ItemDoChecklist[] }>(`/inspecao/checklists/${encodeURIComponent(id)}/itens`),
  cobertura: () => pedir<{ linhas: readonly LinhaDeCobertura[] }>('/inspecao/cobertura'),
  inspecoes: () => pedir<{ inspecoes: readonly Inspecao[] }>('/inspecoes'),
  /** Os itens do checklist da inspeção, cada um com a resposta já gravada. */
  itensDaInspecao: (id: string) =>
    pedir<{
      inspecao: { id: string; situacao: SituacaoDaInspecao; equipamento: string; checklist: string };
      itens: readonly ItemDaInspecao[];
    }>(`/inspecoes/${encodeURIComponent(id)}/itens`),
  ocorrencias: () => pedir<{ ocorrencias: readonly Ocorrencia[] }>('/inspecao/ocorrencias'),
  /** A inspeção de hoje desta máquina e checklist que não foi cancelada — ou `null`. */
  inspecaoViva: (equipamentoId: string, checklistId: string, dia: string) =>
    pedir<{ inspecao: InspecaoViva | null }>(
      `/inspecoes/viva?equipamento=${encodeURIComponent(equipamentoId)}&checklist=${encodeURIComponent(checklistId)}&dia=${encodeURIComponent(dia)}`,
    ),

  criarChecklist: (corpo: {
    readonly nome: string;
    readonly rotina: RotinaDoChecklist;
    readonly modeloId?: string | null;
    readonly configuracaoId?: string | null;
  }) => pedir<{ id: string }>('/inspecao/checklists', { method: 'POST', body: JSON.stringify(corpo) }),
  /** Só aceito enquanto o checklist está em rascunho — o banco recusa depois. */
  acrescentarItem: (checklistId: string, corpo: {
    readonly ordem: number;
    readonly descricao: string;
    readonly grupo?: string;
    readonly tipo?: TipoDoItem;
    readonly unidade?: string | null;
    readonly criticidade?: Criticidade;
  }) => pedir<{ id: string }>(`/inspecao/checklists/${encodeURIComponent(checklistId)}/itens`, {
    method: 'POST',
    body: JSON.stringify(corpo),
  }),
  publicarChecklist: (id: string, notaDeRevisao: string) =>
    pedir<{ id: string; arquivados: number }>(`/inspecao/checklists/${encodeURIComponent(id)}/publicacao`, {
      method: 'POST',
      body: JSON.stringify({ notaDeRevisao }),
    }),

  criarInspecao: (corpo: { readonly equipamentoId: string; readonly modeloId: string; readonly dia?: string }) =>
    pedir<{ id: string }>('/inspecoes', { method: 'POST', body: JSON.stringify(corpo) }),
  responder: (inspecaoId: string, corpo: {
    readonly itemId: string;
    readonly conforme?: boolean | null;
    readonly naoSeAplica?: boolean;
    readonly medida?: number | null;
    readonly texto?: string;
  }) => pedir<{ id: string; ocorrencia: { id: string; criticidade: string } | null }>(
    `/inspecoes/${encodeURIComponent(inspecaoId)}/respostas`,
    { method: 'POST', body: JSON.stringify(corpo) },
  ),
  /** A resposta diz quantas ocorrências continuam abertas — a tela mostra. */
  concluir: (id: string) =>
    pedir<{ id: string; ocorrenciasAbertas: number; criticasAbertas: number }>(
      `/inspecoes/${encodeURIComponent(id)}/conclusao`,
      { method: 'POST' },
    ),

  sinalizarBloqueio: (id: string, pedeBloqueio: boolean) =>
    pedir<{ id: string; pedeBloqueio: boolean; aviso: string }>(
      `/inspecao/ocorrencias/${encodeURIComponent(id)}/bloqueio`,
      { method: 'POST', body: JSON.stringify({ pedeBloqueio }) },
    ),
  encerrar: (id: string, corpo: {
    readonly desfecho: Desfecho;
    readonly ordemId?: string | null;
    readonly justificativa?: string;
  }) => pedir<{ id: string }>(`/inspecao/ocorrencias/${encodeURIComponent(id)}/encerramento`, {
    method: 'POST',
    body: JSON.stringify(corpo),
  }),
};
